/*
 * SEO KEYWORD MAP — CÁLCULO DE HORA TRABALHADA
 * URL: /calculo-de-hora-trabalhada
 * Primary: calculo de hora trabalhada (4.4K vol, KD 20)
 * LSI: valor da hora trabalhada, como calcular hora trabalhada,
 *       valor hora de trabalho, quanto vale minha hora, dividir salário por hora
 * Page type: Guide + Calculator
 */
import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import JsonLd from '../components/JsonLd';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import FAQAccordion from '../components/FAQAccordion';
import RelatedTools from '../components/RelatedTools';
import { formatNumber } from '../utils/timeCalculations';

const CalculoHoraTrabalhada = () => {
  const [salario, setSalario] = useState('');
  const [jornada, setJornada] = useState(220);

  const result = useMemo(() => {
    const sal = parseFloat(salario) || 0;
    if (sal <= 0) return null;
    const valorHora = sal / jornada;
    const valorMin = valorHora / 60;
    const valorDia = valorHora * (jornada === 220 ? 8 : jornada === 180 ? 7.2 : 6);
    return {
      valorHora: valorHora.toFixed(2),
      valorMin: valorMin.toFixed(4),
      valorDia: valorDia.toFixed(2),
      divisor: jornada
    };
  }, [salario, jornada]);

  const salaryTable = [
    { salario: 1621, h220: '7,37', h180: '9,01', h150: '10,81' },
    { salario: 2000, h220: '9,09', h180: '11,11', h150: '13,33' },
    { salario: 2500, h220: '11,36', h180: '13,89', h150: '16,67' },
    { salario: 3000, h220: '13,64', h180: '16,67', h150: '20,00' },
    { salario: 4000, h220: '18,18', h180: '22,22', h150: '26,67' },
    { salario: 5000, h220: '22,73', h180: '27,78', h150: '33,33' },
  ];

  const faqs = [
    { question: 'Como calcular o valor da hora trabalhada?', answer: 'Divida o salário bruto mensal pelo divisor de horas. Para jornada de 44h semanais, o divisor é 220. Para 36h, é 180. Para 30h, é 150. Fórmula: Valor da hora = Salário ÷ Divisor.' },
    { question: 'Por que o divisor é 220 e não 176?', answer: 'Porque o salário mensal também paga o descanso semanal remunerado. O cálculo é: 44 horas ÷ 6 dias úteis = 7,33 horas por dia; × 30 dias do mês = 220. Já 176 (44 × 4) ignoraria tanto os dias de descanso quanto o fato de o mês ter mais de 4 semanas. O divisor 220 é o adotado pela jurisprudência do TST para a jornada de 44 horas.' },
    { question: 'Qual o valor da hora trabalhada no salário mínimo?', answer: 'Com o salário mínimo de R$ 1.621 em 2026 e divisor 220, o valor da hora é R$ 7,37. Para jornada de 36 horas (divisor 180), seria R$ 9,01 por hora.' },
    { question: 'O valor da hora inclui benefícios?', answer: 'Não. O cálculo é feito sobre o salário bruto, antes dos descontos de INSS e IR. Para calcular horas extras, a base inclui também os adicionais de natureza salarial pagos com habitualidade, como insalubridade, periculosidade e adicional noturno (Súmula 264 do TST). Benefícios como vale-transporte, vale-refeição e plano de saúde não entram neste cálculo.' },
    { question: 'O valor da hora muda com hora extra?', answer: 'Sim. A hora extra tem acréscimo mínimo de 50% sobre o valor da hora normal. Então, se sua hora normal vale R$ 10,00, a hora extra vale no mínimo R$ 15,00 (dias úteis) ou R$ 20,00 (domingos/feriados, com 100%).' },
  ];

  const structuredData = { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": faqs.map(faq => ({ "@type": "Question", "name": faq.question, "acceptedAnswer": { "@type": "Answer", "text": faq.answer } })) };

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>Cálculo de Hora Trabalhada: Quanto Vale Sua Hora?</title>
        <meta name="description" content="Aprenda como calcular o valor da hora trabalhada. Calculadora grátis + tabela com valores por faixa salarial. Divisor 220, 180 e 150 explicados." />
        <link rel="canonical" href="https://calculadoradehoras.net.br/calculo-de-hora-trabalhada" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Cálculo de Hora Trabalhada: Quanto Vale Sua Hora?" />
        <meta property="og:description" content="Calcule o valor da sua hora de trabalho. Tabela completa + calculadora grátis." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/calculo-de-hora-trabalhada" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Cálculo de Hora Trabalhada: Quanto Vale Sua Hora?" />
        <meta name="twitter:description" content="Calcule o valor da sua hora de trabalho. Tabela completa + calculadora grátis." />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
      </Helmet>
      <JsonLd data={structuredData} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://calculadoradehoras.net.br/"},
          {"@type":"ListItem","position":2,"name":"Valor da Hora Trabalhada","item":"https://calculadoradehoras.net.br/calculo-de-hora-trabalhada"}
        ]
      }} />

      <Navigation />

      <main className="max-w-4xl mx-auto px-4 py-8">
        <nav className="text-sm text-body-text mb-4">
          <a href="/" className="hover:text-blue-primary">Início</a><span className="mx-2">›</span><span>Cálculo de Hora Trabalhada</span>
        </nav>

        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-dark-text mb-3">Cálculo de Hora Trabalhada</h1>
          <p className="text-lg text-body-text max-w-2xl mx-auto">Descubra quanto vale cada hora do seu trabalho. Calculadora gratuita com tabela de valores por faixa salarial e explicação dos divisores CLT.</p>
        </div>

        <div className="bg-white border-l-4 border-blue-primary rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-lg font-heading font-bold text-blue-primary mb-2">Fórmula do Valor da Hora Trabalhada</h2>
          <p className="text-2xl font-heading font-extrabold text-dark-text">Valor da Hora = Salário Bruto ÷ Divisor</p>
          <p className="text-body-text mt-2">Divisores: <strong>220</strong> (44h/semana) | <strong>180</strong> (36h/semana) | <strong>150</strong> (30h/semana)</p>
        </div>

        {/* Calculator */}
        <div className="bg-white rounded-xl border-2 border-border-gray p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-heading font-bold text-dark-text mb-6">Calcular Valor da Hora</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-body-text mb-2">Salário Bruto Mensal (R$)</label>
              <input type="number" value={salario} onChange={(e) => setSalario(e.target.value)} placeholder="Ex: 3000" min="0" className="w-full h-12 px-4 border-2 border-border-gray rounded-lg focus:border-blue-primary focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-body-text mb-2">Jornada Semanal</label>
              <select value={jornada} onChange={(e) => setJornada(Number(e.target.value))} className="w-full h-12 px-4 border-2 border-border-gray rounded-lg focus:border-blue-primary focus:outline-none">
                <option value={220}>44 horas (÷ 220)</option>
                <option value={180}>36 horas (÷ 180)</option>
                <option value={150}>30 horas (÷ 150)</option>
              </select>
            </div>
          </div>

          {result && (
            <div className="mt-6 bg-light-bg border-l-4 border-success-green rounded-lg p-5">
              <p className="text-sm font-semibold text-body-text uppercase tracking-wide mb-1">Valor da Hora Trabalhada</p>
              <p className="text-3xl font-heading font-bold text-dark-text">R$ {formatNumber(result.valorHora)}</p>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="text-center"><p className="text-xs text-body-text">Por minuto</p><p className="text-lg font-bold text-dark-text">R$ {formatNumber(result.valorMin, 4)}</p></div>
                <div className="text-center"><p className="text-xs text-body-text">Por dia</p><p className="text-lg font-bold text-dark-text">R$ {formatNumber(result.valorDia)}</p></div>
                <div className="text-center"><p className="text-xs text-body-text">Divisor</p><p className="text-lg font-bold text-dark-text">{result.divisor}</p></div>
              </div>
            </div>
          )}
        </div>

        <AdSlot slotId="ad-slot-1" />

        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Tabela: Valor da Hora por Faixa Salarial</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead><tr className="bg-light-bg"><th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">Salário</th><th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">÷ 220 (44h)</th><th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">÷ 180 (36h)</th><th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">÷ 150 (30h)</th></tr></thead>
              <tbody>
                {salaryTable.map((row, i) => (
                  <tr key={i} className="hover:bg-light-bg"><td className="p-3 border-b border-border-gray font-bold">{`R$ ${row.salario.toLocaleString('pt-BR')}`}</td><td className="p-3 border-b border-border-gray text-body-text">{`R$ ${row.h220}`}</td><td className="p-3 border-b border-border-gray text-body-text">{`R$ ${row.h180}`}</td><td className="p-3 border-b border-border-gray text-body-text">{`R$ ${row.h150}`}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <article className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Como Calcular a Hora Trabalhada — Passo a Passo</h2>
            <p className="text-body-text leading-relaxed mb-4">O <strong>cálculo de hora trabalhada</strong> determina quanto vale cada hora do seu trabalho com base no seu salário mensal e na jornada contratada. Esse valor é a base para calcular horas extras, adicional noturno, faltas e atrasos.</p>
            <p className="text-body-text leading-relaxed mb-4">O passo a passo é direto. Primeiro, identifique seu salário bruto mensal — é o valor antes dos descontos de INSS e Imposto de Renda. Depois, identifique o divisor da sua jornada. Para 44 horas semanais, o divisor é 220. Para 36 horas, é 180. Para 30 horas, é 150. Por fim, divida o salário pelo divisor.</p>
            <div className="bg-light-bg p-6 rounded-lg mb-4">
              <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">Exemplo Prático</h3>
              <p className="text-body-text">Salário: R$ 3.000,00 | Jornada: 44h semanais (divisor 220)</p>
              <p className="text-body-text mt-2">Valor da hora = R$ 3.000 ÷ 220 = <strong>R$ 13,64 por hora</strong></p>
              <p className="text-body-text mt-1">Hora extra 50% = R$ 13,64 × 1,50 = <strong>R$ 20,45</strong></p>
            </div>
            <p className="text-body-text leading-relaxed">Para calcular horas extras com base nesse valor, use nossa <a href="/calculo-de-horas-extraordinarias" className="text-blue-primary hover:underline">calculadora de horas extraordinárias</a>. Para calcular o adicional noturno, veja a <a href="/calculadora-de-adicional-noturno" className="text-blue-primary hover:underline">calculadora de adicional noturno</a>.</p>
          </section>
        </article>

        <AdSlot slotId="ad-slot-2" />

        <section className="my-12">
          <h2 className="text-2xl font-heading font-bold text-dark-text mb-6">Perguntas Frequentes</h2>
          <FAQAccordion faqs={faqs} />
        </section>

        <RelatedTools currentPath="/calculo-de-hora-trabalhada" />
        <AdSlot slotId="ad-slot-3" />
      </main>

      <Footer />
    </>
  );
};

export default CalculoHoraTrabalhada;
